import { createConsumer } from "@rails/actioncable";
import { useEffect, useRef } from "react";

import { Constants } from "@socialcoach/services";

import { EDITOR_QUEUE_VIDEO_PROJECT_CHANGED, EditorQueueEvent, isEditorQueueEvent } from "./editorQueueEvents";

const CABLE_PATH = `${Constants.API_URL}/bedrock/cable`;
const CHANNEL_NAME = "EditorQueueChannel";

interface UseEditorQueueCableOptions {
  paused?: boolean;
  pendingProjectId?: number | null;
  onEvent: (event: EditorQueueEvent) => void;
  onReconnect?: () => void;
}

export function useEditorQueueCable({
  paused = false,
  pendingProjectId = null,
  onEvent,
  onReconnect,
}: UseEditorQueueCableOptions) {
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  const pendingProjectIdRef = useRef(pendingProjectId);
  const pausedRef = useRef(paused);
  const queuedEventsRef = useRef<EditorQueueEvent[]>([]);
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  useEffect(() => {
    pendingProjectIdRef.current = pendingProjectId;
  }, [pendingProjectId]);

  useEffect(() => {
    pausedRef.current = paused;
    if (paused || queuedEventsRef.current.length === 0) return;

    const queued = queuedEventsRef.current;
    queuedEventsRef.current = [];
    queued.forEach(event => onEventRef.current(event));
  }, [paused]);

  useEffect(() => {
    const consumer = createConsumer(CABLE_PATH);
    const subscription = consumer.subscriptions.create(CHANNEL_NAME, {
      connected() {
        if (hasConnectedRef.current) {
          onReconnectRef.current?.();
          return;
        }
        hasConnectedRef.current = true;
      },
      received(data: unknown) {
        const event = typeof data === "string" ? parseJsonEvent(data) : data;
        if (!isEditorQueueEvent(event)) return;
        if (
          event.type === EDITOR_QUEUE_VIDEO_PROJECT_CHANGED &&
          pendingProjectIdRef.current != null &&
          event.videoProjectId === pendingProjectIdRef.current
        ) {
          return;
        }
        if (pausedRef.current) {
          queuedEventsRef.current.push(event);
          return;
        }
        onEventRef.current(event);
      },
    });

    return () => {
      subscription.unsubscribe();
      consumer.disconnect();
      hasConnectedRef.current = false;
      queuedEventsRef.current = [];
    };
  }, []);
}

function parseJsonEvent(data: string): unknown {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}
